// ─────────────────────────────────────────────────────────────────────────────
// Centralized API layer
// All fetch calls go through here for consistent error handling and base URL.
// ─────────────────────────────────────────────────────────────────────────────

const BASE = import.meta.env.DEV
  ? "/api"
  : "https://hmm.gov.sa/hmm_mgr/api/new_portal/api";

// ── helpers ──────────────────────────────────────────────────────────────────

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

async function request(endpoint, { params, method = "GET", body, signal, headers } = {}) {
  let url = `${BASE}/${endpoint}`;

  if (params) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== "") qs.append(k, v);
    }
    const str = qs.toString();
    if (str) url += `?${str}`;
  }

  const opts = { method, signal };

  if (body instanceof FormData) {
    opts.body = body;
  } else if (body) {
    opts.headers = { "Content-Type": "application/json", ...headers };
    opts.body = JSON.stringify(body);
  }

  const res = await fetch(url, opts);

  if (!res.ok) {
    throw new ApiError(`HTTP ${res.status}`, res.status);
  }

  const data = await res.json();

  if (data.success === false) {
    throw new ApiError(data.error || "API returned an error", res.status, data);
  }

  return data;
}

function get(endpoint, params, signal) {
  return request(endpoint, { params, signal });
}

function post(endpoint, body, headers) {
  return request(endpoint, { method: "POST", body, headers });
}

// ── Config ───────────────────────────────────────────────────────────────────

export function getConfig() {
  return get("Config.php");
}

// ── Sliders ──────────────────────────────────────────────────────────────────

export function getSliders(signal) {
  return request("Sliders.php", { signal });
}

export function getSliderDetails(id) {
  return get("Slider_Details.php", { id });
}

// ── Home page sections ───────────────────────────────────────────────────────

export function getHomeAboutUs() {
  return get("Home_AboutUsSection.php");
}

export function getHomeServices() {
  return get("Home_ServicesSection.php");
}

export function getHomeNews() {
  return get("Home_NewsSection2.php");
}

export function getHomeEvents() {
  return get("Home_EventsSection.php");
}

// ── News ─────────────────────────────────────────────────────────────────────

export function getNews({ page, limit, search, sort, year } = {}) {
  return get("News.php", { page, limit, search, sort, year });
}

export function getNewsDetails(id) {
  return get("News.php", { id });
}

// ── Events ───────────────────────────────────────────────────────────────────

export function getEvents({ t = 1, page, limit, search, sort, year, type } = {}) {
  return get("Events.php", { t, page, limit, search, sort, year, type });
}

export function getUpcomingEvents(params) {
  return getEvents({ t: 1, ...params });
}

export function getArchivedEvents(params) {
  return getEvents({ t: 2, ...params });
}

export function getEventDetails(id) {
  return get("Events.php", { id });
}

// ── Projects ─────────────────────────────────────────────────────────────────

export function getProjects({ page, limit, search, sort, status } = {}) {
  return get("Projects.php", { page, limit, search, sort, status });
}

export function getProjectDetails(id) {
  return get("Projects.php", { id });
}

// ── E-Services ───────────────────────────────────────────────────────────────

export function getEServices({ page, category, search } = {}) {
  return get("EServices.php", { page, category, search });
}

export function getEServiceDetails(id) {
  return get("EServices.php", { id });
}

export function getRelatedServices({ limit = 3, category, department } = {}) {
  return get("EServices.php", { limit, category, department });
}

// ── Search ───────────────────────────────────────────────────────────────────

export function search({ query, page, sort, type } = {}) {
  return get("Search.php", { query, page, sort, type });
}

// ── About ────────────────────────────────────────────────────────────────────

export function getAboutAmana() {
  return get("About_Amana.php");
}

export function getAboutVision() {
  return get("About_Vision.php");
}

export function getAboutAgents() {
  return get("About_Agents.php");
}

export function getAboutMayors() {
  return get("About_Mayors.php");
}

export function getAboutAdmin() {
  return get("About_AlAmeen.php");
}

// ── Advertisement services ───────────────────────────────────────────────────

export function getSurveys() {
  return get("Esrvc_surveys.php");
}

export function getManqolat(params) {
  return get("Esrvc_manqolat.php", params);
}

export function getInvestment() {
  return get("Esrvc_invesment.php");
}

export function getLocations() {
  return get("Esrvc_locations.php");
}

export function getEngOffices({ page, limit, search, sort } = {}) {
  return get("Esrvc_eng_offices.php", { page, limit, search, sort });
}

export function getQualification() {
  return get("Esrvc_qualification.php");
}

// ── Rating & Feedback ────────────────────────────────────────────────────────

export function getRatingStats(pageUrl) {
  return get("GetRatingStats.php", { page_url: pageUrl });
}

export function saveRating(data) {
  return post("SaveRating.php", data);
}

export function getFeedbackStats(pageUrl) {
  return get("GetFeedbackStats.php", { page_url: pageUrl });
}

export function saveFeedback(data) {
  return post("SaveFeedback.php", data);
}

// ── Contact ──────────────────────────────────────────────────────────────────

export function saveContact(formData) {
  return post("SaveContact.php", formData);
}

// ── Weather (external) ───────────────────────────────────────────────────────

const WEATHER_URL = "https://api.openweathermap.org/data/2.5/weather";

export async function getWeather({ lat, lon, appid, signal } = {}) {
  const qs = new URLSearchParams({ lat, lon, appid, units: "metric", lang: "ar" });
  const res = await fetch(`${WEATHER_URL}?${qs}`, { signal });
  if (!res.ok) throw new ApiError(`Weather HTTP ${res.status}`, res.status);
  return res.json();
}

// ── Re-export error class for consumers ──────────────────────────────────────

export { ApiError };